import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import Createable from "containers/Select";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "firebaseConfig";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import { defaultAllMealSchedules, chartTypes, getWeeksArray } from "utils/common";
import {
  LAYOUT_VARIANT_PER_DAY,
  validatePerDayDateRange,
  enumerateDatesInclusive,
  formatPerDayDateWithWeekday,
  remapDayPlansPositionally,
} from "utils/perDayDietChart";
import {
  savePerDayRangeDiet,
  updatePerDayRangeDiet,
} from "services/perDayRangeDiet";
import WeeklyPerDayMetaForm from "./WeeklyPerDayMetaForm";
import WeeklyPerDayMatrix from "./WeeklyPerDayMatrix";

const emptyErr = {
  clientName: false,
  from: false,
  to: false,
};

function weekPlanOption(val) {
  const weeks = getWeeksArray();
  return weeks.find((w) => w.value === val) || {
    label: val,
    value: val,
    key: val,
  };
}

function chartTypeOption(val) {
  return chartTypes.find((c) => c.value === val) || {
    label: val,
    value: val,
    key: val,
  };
}


export default function WeeklyPerDayFlow({
  uid,
  clients,
  client,
  selectedClientv1,
  mode,
  setMode,
  selectedItem,
  setSelectedItem,
  dietchart,
  setDietChart,
  schedules,
  setFormData: setParentFormData,
}) {
  const [, setSearchParams] = useSearchParams();
  const [step, setStep] = useState("meta");
  const [formData, setFormData] = useState({
    clientName: null,
    from: "",
    to: "",
    weekPlan: null,
    chartType: null,
  });
  const [note, setNote] = useState("");
  const [dayPlans, setDayPlans] = useState({});
  const [errorMessage, setErrorMessage] = useState(emptyErr);
  const [loader, setLoader] = useState(false);
  const [customSchedules, setCustomSchedules] = useState([]);
  const [cloneChartId, setCloneChartId] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState("");
  const [copyTo, setCopyTo] = useState("");

  const perDayCharts = useMemo(
    () =>
      (dietchart || []).filter(
        (d) => d.layoutVariant === LAYOUT_VARIANT_PER_DAY
      ),
    [dietchart]
  );

  const fetchCustomSchedules = useCallback(async () => {
    try {
      const q = query(
        collection(firestore, "customSchedules"),
        where("adminId", "==", uid)
      );
      const snap = await getDocs(q);
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setCustomSchedules(list);
    } catch (e) {
      console.error(e);
    }
  }, [uid]);

  useEffect(() => {
    fetchCustomSchedules();
  }, [fetchCustomSchedules]);

  useEffect(() => {
    if (mode === "create" && selectedClientv1) {
      setFormData((f) => ({ ...f, clientName: selectedClientv1 }));
    }
  }, [mode, selectedClientv1]);

  useEffect(() => {
    if (!setParentFormData) return;
    setParentFormData((prev) => ({
      ...prev,
      clientName: formData.clientName ?? prev?.clientName,
      from: formData.from,
      to: formData.to,
      weekPlan: formData.weekPlan,
      chartType: formData.chartType,
    }));
  }, [formData, setParentFormData]);

  useEffect(() => {
    if (
      (mode === "edit" || mode === "view") &&
      selectedItem?.layoutVariant === LAYOUT_VARIANT_PER_DAY
    ) {
      setFormData({
        clientName: {
          label: client?.name + ` (+91${client?.phoneNumber})`,
          value: client?.id,
          key: client?.id,
          name: client?.name,
        },
        from: selectedItem.from,
        to: selectedItem.to,
        weekPlan: weekPlanOption(selectedItem.weekPlan),
        chartType: chartTypeOption(selectedItem.chartType),
      });
      setNote(selectedItem.notes || "");
      setDayPlans(selectedItem.dayPlans ? { ...selectedItem.dayPlans } : {});
      setStep("matrix");
    }
  }, [mode, selectedItem, client]);

  const datesInRange = useMemo(() => {
    if (!formData.from || !formData.to) return [];
    return enumerateDatesInclusive(formData.from, formData.to);
  }, [formData.from, formData.to]);

  const continueToMatrix = () => {
    if (!formData.clientName) {
      toast.error("Choose a client.");
      setErrorMessage((e) => ({ ...e, clientName: true }));
      return;
    }
    if (!formData.from || !formData.to) {
      toast.error("Set start and end dates.");
      return;
    }
    const v = validatePerDayDateRange(formData.from, formData.to);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    if (!formData.weekPlan || !formData.chartType) {
      toast.error("Week plan and chart type are required.");
      return;
    }
    setDayPlans((prev) => {
      const next = { ...prev };
      datesInRange.forEach((d) => {
        if (!next[d]) next[d] = {};
      });
      return next;
    });
    setStep("matrix");
  };

  const persistDayPlans = async (plans) => {
    if (mode !== "edit" || !selectedItem?.dietChartId) return;
    await updatePerDayRangeDiet({
      formdata: { clientName: formData.clientName },
      dietPlan: {
        from: formData.from,
        to: formData.to,
        notes: note,
        weekPlan: formData.weekPlan,
        chartType: formData.chartType,
        dietChartId: selectedItem.dietChartId,
        createdAt: selectedItem.createdAt,
      },
      uid,
      dayPlans: plans,
    });
    setDietChart((prev) =>
      (prev || []).map((row) =>
        row.dietChartId === selectedItem.dietChartId
          ? { ...row, dayPlans: plans }
          : row
      )
    );
  };

  const saveHandler = async () => {
    try {
      setLoader(true);
      const dietPlan = {
        from: formData.from,
        to: formData.to,
        notes: note,
        weekPlan: formData.weekPlan,
        chartType: formData.chartType,
        dietChartId: selectedItem?.dietChartId,
        createdAt: selectedItem?.createdAt,
      };
      if (mode === "edit" && selectedItem?.dietChartId) {
        await updatePerDayRangeDiet({
          formdata: { clientName: formData.clientName },
          dietPlan,
          uid,
          dayPlans,
        });
        setDietChart((prev) =>
          (prev || []).map((row) =>
            row.dietChartId === selectedItem.dietChartId
              ? {
                  ...row,
                  from: formData.from,
                  to: formData.to,
                  notes: note,
                  weekPlan: formData.weekPlan.value,
                  chartType: formData.chartType.value,
                  dayPlans,
                }
              : row
          )
        );
        toast.success("Plan updated.");
      } else {
        const id = await savePerDayRangeDiet({
          formdata: { clientName: formData.clientName },
          dietPlan,
          uid,
          dayPlans,
        });
        const row = {
          clientId: formData.clientName.value,
          from: formData.from,
          to: formData.to,
          notes: note,
          weekPlan: formData.weekPlan.value,
          chartType: formData.chartType.value,
          layoutVariant: LAYOUT_VARIANT_PER_DAY,
          dayPlans,
          dietChartId: id,
          createdAt: new Date(),
        };
        setDietChart((prev) => [row, ...(prev || [])]);
        toast.success("Plan saved.");
      }
      setSearchParams({});
      setSelectedItem(null);
      setMode("view");
    } catch (e) {
      const msg = e?.message || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally {
      setLoader(false);
    }
  };

  const applyCopyDay = () => {
    if (!copyFrom || !copyTo || copyFrom === copyTo) {
      toast.error("Pick two different dates.");
      return;
    }
    if (!dayPlans[copyFrom]) {
      toast.error("Source day has no meals.");
      return;
    }
    setDayPlans((prev) => ({
      ...prev,
      [copyTo]: JSON.parse(JSON.stringify(prev[copyFrom] || {})),
    }));
    toast.success("Day copied.");
    setCopyOpen(false);
  };

  // Copies meal grids from another saved chart onto this draft’s dates (positional); keeps current range and notes.
  const cloneFromChart = (id) => {
    setCloneChartId(id || "");
    if (!id) return;
    const src = perDayCharts.find((c) => c.dietChartId === id);
    if (!src?.dayPlans) return;
    const rangeCheck = validatePerDayDateRange(formData.from, formData.to);
    if (!rangeCheck.ok) {
      toast.error(rangeCheck.message || "Invalid date range.");
      return;
    }
    const merged = remapDayPlansPositionally(
      src.dayPlans,
      src.from,
      src.to,
      formData.from,
      formData.to
    );
    setDayPlans((prev) => ({ ...prev, ...merged }));
    toast.success("Meals copied from selected chart.");
  };

  return (
    <Card
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "visible",
      }}
    >
      <SuiTypography variant="h6" mb={0.5} fontWeight={700}>
        Weekly (per-day) diet plan
      </SuiTypography>
      <SuiTypography variant="body2" color="text.secondary" mb={3}>
        Add meals per schedule slot for each day. Scroll horizontally if needed.
      </SuiTypography>

      {step === "meta" && (
        <>
          <WeeklyPerDayMetaForm
            formData={formData}
            setFormData={setFormData}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            clients={clients}
            mode={mode}
            client={client}
          />
          <Button
            variant="contained"
            onClick={continueToMatrix}
            sx={{
              color: "common.white",
              "&:hover": {
                color: "common.white",
                transform: "revert",
              },
            }}
          >
            Continue to meal grid
          </Button>
        </>
      )}

      {step === "matrix" && (
        <>
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1.5,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Clone &amp; copy tools
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <Typography variant="caption" mb={0.5} display="block" color="text.secondary">
                  Clone per-day chart
                </Typography>
                <Createable
                  options={perDayCharts.map((c) => ({
                    label: `${c.from} → ${c.to} (${c.weekPlan})`,
                    value: c.dietChartId,
                    key: c.dietChartId,
                  }))}
                  value={
                    cloneChartId
                      ? {
                          label: perDayCharts.find(
                            (c) => c.dietChartId === cloneChartId
                          )
                            ? `${
                                perDayCharts.find(
                                  (c) => c.dietChartId === cloneChartId
                                ).from
                              } → ${
                                perDayCharts.find(
                                  (c) => c.dietChartId === cloneChartId
                                ).to
                              } (${
                                perDayCharts.find(
                                  (c) => c.dietChartId === cloneChartId
                                ).weekPlan
                              })`
                            : "",
                          value: cloneChartId,
                        }
                      : null
                  }
                  disabled={mode === "view" || !perDayCharts.length}
                  placeholder="Choose chart to clone"
                  onChange={(e) => cloneFromChart(e?.value)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  disabled={mode === "view"}
                  onClick={() => setCopyOpen(true)}
                  disableElevation
                  sx={(theme) => ({
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2.5,
                    minHeight: 40,
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.grey[200],
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "none",
                    transition: "background-color 120ms ease",
                    "&:hover": {
                      backgroundColor: theme.palette.grey[300],
                      boxShadow: "none",
                    },
                    "&:active": { boxShadow: "none" },
                  })}
                >
                  Copy day → day
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mb: 3 }}>
            <SuiTypography mb={1} variant="h6">
              Notes
            </SuiTypography>
            <SuiInput
              disabled={mode === "view"}
              multiline
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Guidelines for client"
            />
          </Box>

          <WeeklyPerDayMatrix
            from={formData.from}
            to={formData.to}
            dayPlans={dayPlans}
            setDayPlans={setDayPlans}
            schedules={schedules}
            customSchedules={customSchedules}
            defaultMealSchedules={defaultAllMealSchedules}
            mode={mode}
            uid={uid}
            onPersistDayPlans={persistDayPlans}
          />

          <Stack direction="row" spacing={1.5} mt={3} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() => setStep("meta")}
              sx={(theme) => ({
                textTransform: "none",
                fontWeight: 700,
                minWidth: 100,
                color: theme.palette.text.primary,
                borderColor: theme.palette.grey[500],
                "&:hover": {
                  borderColor: theme.palette.grey[700],
                  backgroundColor: theme.palette.grey[100],
                },
              })}
            >
              Back
            </Button>
            {mode !== "view" && (
              <Button
                variant="contained"
                color="primary"
                disabled={loader}
                onClick={saveHandler}
                sx={{ textTransform: "none", fontWeight: 600, px: 3, minHeight: 40 }}
              >
                {loader ? "Saving…" : "Save"}
              </Button>
            )}
          </Stack>
        </>
      )}

      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)}>
        <DialogTitle>Copy meals between days</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="caption" mb={0.5} display="block">
              From
            </Typography>
            <Createable
              options={datesInRange.map((d) => ({
                label: formatPerDayDateWithWeekday(d),
                value: d,
                key: d,
              }))}
              value={
                copyFrom
                  ? { label: formatPerDayDateWithWeekday(copyFrom), value: copyFrom }
                  : null
              }
              onChange={(e) => setCopyFrom(e?.value)}
              placeholder="Choose source day"
            />
          </Box>
          <Box>
            <Typography variant="caption" mb={0.5} display="block">
              To
            </Typography>
            <Createable
              options={datesInRange.map((d) => ({
                label: formatPerDayDateWithWeekday(d),
                value: d,
                key: d,
              }))}
              value={
                copyTo
                  ? { label: formatPerDayDateWithWeekday(copyTo), value: copyTo }
                  : null
              }
              onChange={(e) => setCopyTo(e?.value)}
              placeholder="Choose target day"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)}>Cancel</Button>
          <Button onClick={applyCopyDay} variant="contained">
            Replace target day
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
